import { Controller } from "@hotwired/stimulus"
import EasyMDE from "easymde"

export default class extends Controller {
  static values = { 
    placeholder: String,
    spellChecker: { type: Boolean, default: true },
    autosave: { type: Boolean, default: true }
  }

  connect() {
    // Import CSS
    this.loadCSS()
    
    // Initialize EasyMDE
    this.initializeEditor()
  }

  disconnect() {
    if (this.editor) {
      this.editor.toTextArea()
      this.editor = null
    }
  }

  loadCSS() {
    // Check if CSS is already loaded
    if (document.querySelector('#easymde-css')) return
    
    const link = document.createElement('link')
    link.id = 'easymde-css'
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/easymde/dist/easymde.min.css'
    document.head.appendChild(link)
  }

  initializeEditor() {
    this.editor = new EasyMDE({
      element: this.element,
      placeholder: this.placeholderValue || "Write your markdown content here...",
      spellChecker: this.spellCheckerValue,
      autosave: this.autosaveValue ? {
        enabled: true,
        uniqueId: `blog_post_${Date.now()}`,
        delay: 3000,
      } : false,
      toolbar: [
        "bold", "italic", "strikethrough", "|",
        "heading-1", "heading-2", "heading-3", "|",
        "quote", "unordered-list", "ordered-list", "|",
        "link", "image", "table", "|",
        "code", "horizontal-rule", "|",
        "preview", "side-by-side", "fullscreen", "|",
        "guide",
        {
          name: "image-anchor",
          action: this.insertImageAnchor.bind(this),
          className: "fa fa-anchor",
          title: "Insert Image Anchor"
        },
        {
          name: "cta-button",
          action: this.insertCta.bind(this),
          className: "fa fa-bullhorn",
          title: "Insert CTA Button"
        }
      ],
      shortcuts: {
        toggleBold: "Cmd-B",
        toggleItalic: "Cmd-I",
        toggleStrikethrough: "Cmd-Alt-S",
        toggleHeading1: "Cmd-H",
        toggleHeading2: "Cmd-Alt-H",
        toggleUnorderedList: "Cmd-L",
        toggleOrderedList: "Cmd-Alt-L",
        drawLink: "Cmd-K",
        drawImage: "Cmd-Alt-I",
        togglePreview: "Cmd-P",
        toggleSideBySide: "Cmd-Alt-P",
        toggleFullScreen: "F11"
      },
      status: ["autosave", "lines", "words", "cursor"],
      renderingConfig: {
        singleLineBreaks: false,
        codeSyntaxHighlighting: true,
      },
      insertTexts: {
        horizontalRule: ["", "\n\n-----\n\n"],
        image: ["![](", ")"],
        link: ["[", "](http://)"],
        table: ["", "\n\n| Column 1 | Column 2 | Column 3 |\n| -------- | -------- | -------- |\n| Text     | Text     | Text     |\n\n"],
      }
    })

    // Add custom styling
    this.addCustomStyling()
  }

  insertImageAnchor() {
    const cm = this.editor.codemirror
    const selection = cm.getSelection()
    const anchorText = selection || "anchor-name"
    const replacement = `[image:${anchorText}]`
    
    if (selection) {
      cm.replaceSelection(replacement)
    } else {
      const cursor = cm.getCursor()
      cm.setSelection(cursor, cursor)
      cm.replaceSelection(replacement)
    }
    
    cm.focus()
  }

  insertCta() {
    const cm = this.editor.codemirror
    const selection = cm.getSelection()
    const label = selection || "Start planning your event"
    const replacement = `[cta text="${label}" url="/organizer/sign_up" style="primary"]`
    if (selection) {
      cm.replaceSelection(replacement)
    } else {
      const cursor = cm.getCursor()
      cm.setSelection(cursor, cursor)
      cm.replaceSelection(replacement)
    }
    cm.focus()
  }

  addCustomStyling() {
    // Add custom styles to match admin theme
    const editorElement = this.element.nextElementSibling
    if (editorElement && editorElement.classList.contains('EasyMDEContainer')) {
      editorElement.style.fontFamily = 'inherit'
      
      // Match form styling
      const toolbar = editorElement.querySelector('.editor-toolbar')
      if (toolbar) {
        toolbar.style.borderColor = '#d1d5db'
        toolbar.style.backgroundColor = '#f9fafb'
      }
      
      const editor = editorElement.querySelector('.CodeMirror')
      if (editor) {
        editor.style.borderColor = '#d1d5db'
        editor.style.fontSize = '14px'
        editor.style.fontFamily = 'ui-monospace, SFMono-Regular, monospace'
      }
    }
  }
}